import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { ago } from "@/lib/format";
import { Panel, StatusBadge, ProvenanceTag } from "@/components/common/primitives";
import { OFFER_PERIODS, periodToDays, MAX_HISTORY_DAYS } from "@/lib/analyzerData";
import { WalmartOffersWorkspace } from "./WalmartOffersWorkspace";
import { DateRangePopover } from "@/components/dashboard/DateRangePopover";
import { RefreshCw, CalendarDays } from "lucide-react";

const OFFERS_SUBS = [
  { id: "offers", label: "Offers" },
  { id: "buybox", label: "Buy Box Statistics" },
  { id: "used-buybox", label: "Used Buy Box Statistics" },
];

function DataFreshness({ collectedAt, liveMode }) {
  return (
    <div className="flex items-center gap-2 text-micro text-ink-muted">
      {liveMode ? (
        <StatusBadge tone="info">Live catalog</StatusBadge>
      ) : (
        <ProvenanceTag kind="fixture" />
      )}
      <span>Updated {collectedAt ? ago(collectedAt) : "—"}</span>
      <button className="inline-flex items-center gap-1 h-7 px-2 rounded-ctrl border border-edge-subtle hover:bg-surface-2 ez-press" aria-label="Refresh Walmart data">
        <RefreshCw className="h-3.5 w-3.5" />Refresh
      </button>
    </div>
  );
}

function RiskEligibilitySection({ bundle, liveMode }) {
  const checks = bundle?.eligibility || [];
  if (liveMode || bundle?.available === false || bundle?.eligibility_reason === "eligibility_unavailable") {
    if (!checks.length) {
      return (
        <Panel className="p-10 text-center" testid="eligibility-unavailable">
          <div className="text-compact font-medium text-ink-primary">Eligibility checks not available</div>
          <p className="text-compact text-ink-secondary mt-1">
            Restriction / hazmat / gating signals are not returned by catalog search for this product.
          </p>
          <p className="text-micro font-mono text-ink-muted mt-2">
            {bundle?.eligibility_reason || "eligibility_unavailable"}
          </p>
        </Panel>
      );
    }
  }
  const meta = (s) => s === "pass" ? { tone: "success", label: "Passed" }
    : s === "warning" ? { tone: "warning", label: "Needs review" }
    : s === "fail" ? { tone: "danger", label: "Failed" }
    : { tone: "neutral", label: "Unknown" };
  return (
    <Panel className="p-0 overflow-hidden" testid="eligibility-table">
      <div className="px-4 py-2.5 border-b border-edge-subtle flex items-center justify-between">
        <span className="text-stitle text-ink-primary">Risk &amp; Eligibility</span>
        <span className="text-micro text-ink-muted">Unknown is never treated as passed</span>
      </div>
      <div className="overflow-x-auto ez-scroll">
        <table className="w-full text-compact" style={{ minWidth: 640 }}>
          <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2">
            <th className="py-2 px-3 border-b border-edge-subtle">Check</th>
            <th className="px-3 border-b border-edge-subtle">Status</th>
            <th className="px-3 border-b border-edge-subtle">Detail</th>
          </tr></thead>
          <tbody>
            {checks.map((e, i) => {
              const m = meta(e.status);
              return (
                <tr key={i} className="hover:bg-surface-1 align-top">
                  <td className="py-2 px-3 border-b border-edge-subtle text-ink-primary whitespace-nowrap">{e.item}</td>
                  <td className="px-3 py-2 border-b border-edge-subtle"><StatusBadge tone={m.tone}>{m.label}</StatusBadge></td>
                  <td className="px-3 py-2 border-b border-edge-subtle text-ink-secondary">{e.detail}{e.provenance && e.provenance !== "fixture" ? ` · ${e.provenance}` : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2 border-t border-edge-subtle text-micro font-mono text-ink-muted">Unknown is not treated as passed.</p>
    </Panel>
  );
}

export function WalmartDataTab({ product, bundle, setField, liveMode = false }) {
  const [section, setSection] = useState("offers");
  const [selectedId, setSelectedId] = useState(null);
  const [period, setPeriod] = useState("90D");
  const [customRange, setCustomRange] = useState(null); // { start, end, label }
  const allStart = new Date(Date.now() - MAX_HISTORY_DAYS * 864e5);
  const days = customRange ? Math.max(1, Math.round((customRange.end - customRange.start) / 864e5)) : periodToDays(period);
  const periodLabel = customRange ? customRange.label : period;
  const showPeriod = section === "offers" || section === "buybox";
  const btn = (id, label, active) => (
    <button key={id} role="tab" aria-selected={active} data-testid={`data-section-${id}`} onClick={() => setSection(id)}
      className={cn("px-3 h-8 text-compact", active ? "ez-seg-active" : "ez-seg")}>{label}</button>
  );
  return (
    <section data-testid="walmart-data-tab" className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-stitle text-ink-primary">Offers</h2>
          <p className="text-compact text-ink-secondary">
            {liveMode
              ? "Seller offers and buy-box statistics when available from a live data source."
              : "Unified seller offers, Buy Box statistics and used Buy Box for the Walmart marketplace."}
          </p>
        </div>
        <DataFreshness collectedAt={product?.walmart?.collected_at} liveMode={liveMode} />
      </header>

      <nav className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Offers workspace">
        <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll">
          {OFFERS_SUBS.map((s) => btn(s.id, s.label, section === s.id))}
        </div>
        <span className="text-ink-muted text-micro">·</span>
        <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll">
          {btn("risk", "Risk & Eligibility", section === "risk")}
        </div>
        {showPeriod && !liveMode && (
          <div className="ml-auto flex items-center gap-2">
            <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro" role="group" aria-label="History period">
              {OFFER_PERIODS.map((p) => (
                <button key={p} data-testid={`offers-period-${p}`} aria-pressed={!customRange && period === p} onClick={() => { setCustomRange(null); setPeriod(p); }} className={cn("px-2 h-7 font-mono", !customRange && period === p ? "ez-seg-active" : "ez-seg")}>{p}</button>
              ))}
            </div>
            <DateRangePopover
              value={customRange}
              allStart={allStart}
              maxDate={new Date()}
              align="end"
              onApply={(r) => setCustomRange(r)}
              trigger={
                <button data-testid="offers-custom-range" className={cn("inline-flex items-center gap-1.5 h-7 px-2.5 rounded-ctrl border text-micro font-mono whitespace-nowrap ez-press", customRange ? "ez-seg-active" : "border-edge-subtle hover:bg-surface-2")}>
                  <CalendarDays className="h-3.5 w-3.5" />{customRange ? customRange.label : "Custom range"}
                </button>
              }
            />
            {customRange && (
              <button data-testid="offers-custom-clear" onClick={() => setCustomRange(null)} className="h-7 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2 whitespace-nowrap">Clear</button>
            )}
          </div>
        )}
      </nav>

      {!bundle ? (
        <div className="p-10 text-center text-compact text-ink-muted" data-testid="data-loading">Loading Walmart data…</div>
      ) : (
        <div data-testid="data-section-content">
          {section === "risk"
            ? <RiskEligibilitySection bundle={bundle} liveMode={liveMode} />
            : <WalmartOffersWorkspace bundle={bundle} section={section} period={periodLabel} days={days} selectedId={selectedId} setSelectedId={setSelectedId} onUseInCalc={(v) => setField("sale_price", v)} liveMode={liveMode} />}
        </div>
      )}
    </section>
  );
}

export default WalmartDataTab;
